import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { cn } from "@/lib/utils";

interface WaterfallDataPoint {
  name: string;
  value: number;
  isTotal?: boolean;
}

interface WaterfallChartProps {
  data: WaterfallDataPoint[];
  loading?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
}

export const WaterfallChart = ({
  data,
  loading = false,
  className,
  title = "Performance Attribution",
  subtitle = "Breakdown of PnL sources",
}: WaterfallChartProps) => {
  if (loading) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-40 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="h-72 w-full bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }

  // Transform data for waterfall effect
  // Each bar needs start and end positions
  let cumulative = 0;
  const chartData = data.map((item, index) => {
    const start = item.isTotal ? 0 : cumulative;
    const end = item.isTotal ? item.value : cumulative + item.value;
    cumulative = item.isTotal ? item.value : end;

    return {
      name: item.name,
      value: item.value,
      start,
      end,
      isPositive: item.value >= 0,
      isTotal: item.isTotal,
      // For the bar chart, we need to show the delta from start
      barStart: Math.min(start, end),
      barHeight: Math.abs(item.value),
    };
  });

  const formatCurrency = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 1000) {
      return `${sign}$${(value / 1000).toFixed(1)}k`;
    }
    return `${sign}$${value.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      return (
        <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-xl">
          <p className="text-xs font-medium text-foreground mb-1">{point.name}</p>
          <p className={cn(
            "text-lg font-bold font-mono",
            point.isPositive ? "text-emerald-500" : "text-rose-500"
          )}>
            {formatCurrency(point.value)}
          </p>
          {!point.isTotal && (
            <p className="text-xs text-muted-foreground mt-1">
              Running total: {formatCurrency(point.end)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-semibold mb-1">{title}</h3>
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
          No attribution data available
        </div>
      </div>
    );
  }

  // Calculate domain for Y axis
  const allValues = chartData.flatMap(d => [d.start, d.end]);
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const padding = Math.abs(maxVal - minVal) * 0.1;

  return (
    <div className={cn("stat-card h-[400px] group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold mb-1">{title}</h3>
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} barCategoryGap="20%">
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.3}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                dy={10}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickFormatter={(value) => formatCurrency(value)}
                dx={-5}
                domain={[minVal - padding, maxVal + padding]}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.2 }} />
              <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
              
              {/* Invisible bar for positioning */}
              <Bar dataKey="barStart" stackId="waterfall" fill="transparent" />
              
              {/* Visible bar showing the value */}
              <Bar dataKey="barHeight" stackId="waterfall" radius={[4, 4, 4, 4]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.isTotal
                        ? entry.value >= 0
                          ? "hsl(var(--primary))"
                          : "hsl(var(--destructive))"
                        : entry.isPositive
                          ? "hsl(142, 76%, 36%)" // emerald-600
                          : "hsl(0, 84%, 60%)"   // rose-500
                    }
                    fillOpacity={entry.isTotal ? 1 : 0.8}
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
